/**
 * Shared sort action sheet for mobile scouting toolbars.
 *
 * Composed on the shared MobileSheetChrome primitive (SE-5525), which owns
 * the sheet surface, drag handle, ✕ close chip, and every dismissal path.
 * This view supplies the content: the SORT title, an ascending/descending
 * toggle pinned in the chrome's sticky header band, the scrollable list of
 * sort options (chrome body), and an "Apply sort" button in the sticky
 * footer. Used by both MobileTargetsToolbar and MobileScoutReportsToolbar
 * to eliminate duplication.
 * @module features/scouting/components/MobileSortSheet/MobileSortSheetView
 */

import React, { memo } from "react";

import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import SortOptionItem from "@/features/scouting/components/organisms/MobileTargetsToolbar/SortOptionItem";

import type { SortOption } from "@/features/scouting/types/scouting-target";

/** Theme key derived from isDark prop */
type SortSheetTheme = "dark" | "light";

/** Toggle track background per theme */
const TOGGLE_TRACK_CLASS: Record<SortSheetTheme, string> = {
  light: "rounded-md bg-surface-muted p-micro",
  dark: "rounded-md bg-background-muted p-micro",
};

/**
 * Active tab background per theme.
 *
 * `px-3` keeps the centered label off the tab edges so it is not clipped on
 * narrow viewports — each tab is `flex-1` and shrinks on small screens
 * (SE-5414).
 */
const TAB_ACTIVE_CLASS: Record<SortSheetTheme, string> = {
  light: "flex-1 items-center rounded-sm bg-white px-3 py-2",
  dark: "flex-1 items-center rounded-sm bg-surface-muted px-3 py-2",
};

/** Inactive tab (transparent in both modes) */
const TAB_INACTIVE_CLASS =
  "flex-1 items-center rounded-sm bg-transparent px-3 py-2";

/**
 * Active tab text per theme.
 *
 * `web:whitespace-nowrap` keeps each label ("Ascending" / "Descending") on a
 * single line on web. Each tab is `flex-1`, so on a narrow web viewport the
 * flex item shrinks below the label's intrinsic width; without an explicit
 * no-wrap the web text-layout model wraps/clips the label — the residual
 * SE-5414 defect that the `px-3` padding fix (PR #5815) did not resolve on web
 * (native RN text does not use the CSS white-space model, so iOS/Android were
 * already correct). Scoped to the `web:` platform variant because it targets a
 * web-only rendering behaviour, not a device-type layout decision.
 */
const TAB_TEXT_ACTIVE_CLASS: Record<SortSheetTheme, string> = {
  light: "web:whitespace-nowrap text-content-primary",
  dark: "web:whitespace-nowrap text-white",
};

/** Inactive tab text (same in both modes) — see TAB_TEXT_ACTIVE_CLASS re: no-wrap */
const TAB_TEXT_INACTIVE_CLASS = "web:whitespace-nowrap text-content-subtle";

/** Props for MobileSortSheetView */
export interface MobileSortSheetViewProps {
  /** Whether the sort sheet is open */
  readonly isOpen: boolean;
  /** Callback to close the sort sheet */
  readonly onClose: () => void;
  /** Available sort options */
  readonly sortOptions: readonly SortOption[];
  /** Pending sort field selection (in-sheet, not yet applied) */
  readonly pendingSortField: string | null;
  /** Pending sort direction */
  readonly pendingSortDir: "asc" | "desc";
  /** Callback when a sort radio option is tapped */
  readonly onSelectSort: (field: string) => void;
  /** Callback to select ascending sort */
  readonly onSelectAsc: () => void;
  /** Callback to select descending sort */
  readonly onSelectDesc: () => void;
  /** Callback when "Apply sort" button is pressed */
  readonly onApplySort: () => void;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Optional testID prefix for each sort option; suffix is the field key (e2e) */
  readonly optionTestIDPrefix?: string;
  /** Optional testID for the "Apply sort" button (e2e) */
  readonly applyTestID?: string;
}

/**
 * Resolves theme key from isDark prop
 * @param isDark - Whether dark mode is active
 */
const resolveTheme = (isDark: boolean): SortSheetTheme =>
  isDark ? "dark" : "light";

/**
 * Renders the sort sheet content: direction toggle (sticky header), options
 * list (scrolling body), and apply button (sticky footer) inside the shared
 * MobileSheetChrome.
 * @param root0 - Component props
 * @param root0.isOpen - Whether the sheet is visible
 * @param root0.onClose - Close handler
 * @param root0.sortOptions - Available sort options
 * @param root0.pendingSortField - Pending sort selection
 * @param root0.pendingSortDir - Pending sort direction
 * @param root0.onSelectSort - Sort option selection handler
 * @param root0.onSelectAsc - Ascending direction handler
 * @param root0.onSelectDesc - Descending direction handler
 * @param root0.onApplySort - Apply sort handler
 * @param root0.isDark - Dark mode flag
 * @param root0.optionTestIDPrefix - Optional testID prefix for each sort option
 * @param root0.applyTestID - Optional testID for the "Apply sort" button
 */
const MobileSortSheetView = ({
  isOpen,
  onClose,
  sortOptions,
  pendingSortField,
  pendingSortDir,
  onSelectSort,
  onSelectAsc,
  onSelectDesc,
  onApplySort,
  isDark,
  optionTestIDPrefix,
  applyTestID,
}: MobileSortSheetViewProps) => (
  <MobileSheetChrome
    isOpen={isOpen}
    onClose={onClose}
    title="SORT"
    headerContent={
      /* Ascending / Descending toggle — pinned in the chrome's sticky header */
      <Stack
        direction="horizontal"
        className={TOGGLE_TRACK_CLASS[resolveTheme(isDark)]}
      >
        <Pressable
          onPress={onSelectAsc}
          className={
            pendingSortDir === "asc"
              ? TAB_ACTIVE_CLASS[resolveTheme(isDark)]
              : TAB_INACTIVE_CLASS
          }
        >
          <Text
            variant={pendingSortDir === "asc" ? "body-bold" : "body"}
            className={
              pendingSortDir === "asc"
                ? TAB_TEXT_ACTIVE_CLASS[resolveTheme(isDark)]
                : TAB_TEXT_INACTIVE_CLASS
            }
          >
            Ascending
          </Text>
        </Pressable>
        <Pressable
          onPress={onSelectDesc}
          className={
            pendingSortDir === "desc"
              ? TAB_ACTIVE_CLASS[resolveTheme(isDark)]
              : TAB_INACTIVE_CLASS
          }
        >
          <Text
            variant={pendingSortDir === "desc" ? "body-bold" : "body"}
            className={
              pendingSortDir === "desc"
                ? TAB_TEXT_ACTIVE_CLASS[resolveTheme(isDark)]
                : TAB_TEXT_INACTIVE_CLASS
            }
          >
            Descending
          </Text>
        </Pressable>
      </Stack>
    }
    footer={
      /* SE-4146: semantic accent token (was a raw bg-blue-500) */
      <Pressable
        testID={applyTestID}
        onPress={onApplySort}
        className="items-center rounded-sm bg-accent-primary py-3"
      >
        <Text variant="title-sm" className="text-content-on-accent">
          Apply sort
        </Text>
      </Pressable>
    }
  >
    <Stack>
      {sortOptions.map(opt => (
        <SortOptionItem
          key={opt.field}
          field={opt.field}
          label={opt.label}
          isActive={pendingSortField === opt.field}
          onSelect={onSelectSort}
          isDark={isDark}
          testID={
            optionTestIDPrefix ? `${optionTestIDPrefix}${opt.field}` : undefined
          }
        />
      ))}
    </Stack>
  </MobileSheetChrome>
);

MobileSortSheetView.displayName = "MobileSortSheetView";

export default memo(MobileSortSheetView);
